export const QUICK_TOPUP_EVENT = "boide:quick-topup";
export const DEFAULT_QUICK_TOPUP_MIN_AMOUNT = 50_000;
export const DEFAULT_QUICK_TOPUP_MAX_AMOUNT = 500_000;

export type QuickTopupOffer = {
  id: number;
  eligible: boolean;
  offer_date: string;
  eligibility_reason: string;
  source: string;
  balance_at_trigger: number;
  required_amount?: number | null;
  bonus_rate: number;
  min_amount: number;
  max_amount: number;
  started_at: string;
  expires_at: string;
  consumed: boolean;
  expired: boolean;
  /** Giờ server lúc trả offer — dùng tính lệch đồng hồ client/server. */
  server_now?: string;
  /** Date.now() của client lúc FE nhận offer (FE tự gắn, không từ backend). */
  client_received_at?: number;
};

export type QuickTopupEventDetail = { reason?: string; offer?: QuickTopupOffer };

/**
 * Gắn mốc thời gian client lúc nhận offer (immutable — trả bản copy).
 * Cặp (server_now, client_received_at) cho phép countdown neo theo giờ server:
 * skew = server_now - client_received_at, không phụ thuộc giờ máy user.
 */
export function stampQuickTopupOffer(offer?: QuickTopupOffer): QuickTopupOffer | undefined {
  if (!offer) return offer;
  const minAmount = Number(offer.min_amount);
  const maxAmount = Number(offer.max_amount);
  return {
    ...offer,
    min_amount:
      Number.isFinite(minAmount) && minAmount > 0
        ? minAmount
        : DEFAULT_QUICK_TOPUP_MIN_AMOUNT,
    max_amount:
      Number.isFinite(maxAmount) && maxAmount >= (Number.isFinite(minAmount) ? minAmount : 0)
        ? maxAmount
        : DEFAULT_QUICK_TOPUP_MAX_AMOUNT,
    client_received_at: offer.client_received_at ?? Date.now(),
  };
}

/** Độ lệch (ms) giữa giờ server và giờ client; 0 nếu thiếu dữ liệu (backend cũ). */
export function quickTopupClockSkewMs(offer?: QuickTopupOffer): number {
  if (!offer?.server_now || !offer.client_received_at) return 0;
  const serverMs = new Date(offer.server_now).getTime();
  return Number.isFinite(serverMs) ? serverMs - offer.client_received_at : 0;
}

/**
 * Offer còn dùng được để đính vào đơn nạp: chưa tiêu thụ, chưa hết hạn theo cờ
 * backend, và `expires_at` chưa qua theo giờ server (neo skew). Dùng type guard
 * để caller truy cập `offer.id` an toàn sau khi kiểm tra.
 */
export function isQuickTopupOfferUsable(offer?: QuickTopupOffer | null): offer is QuickTopupOffer {
  if (!offer || offer.consumed || offer.expired) return false;
  const expiresMs = new Date(offer.expires_at).getTime();
  // expires_at không parse được (backend cũ) → để backend tự quyết định
  if (!Number.isFinite(expiresMs)) return true;
  const serverNow = Date.now() + quickTopupClockSkewMs(offer);
  return expiresMs > serverNow;
}

/** Offer còn hạn và số tiền thực trả nằm trong khoảng được ưu đãi (kể cả hai đầu). */
export function isQuickTopupOfferApplicable(
  offer: QuickTopupOffer | null | undefined,
  amount: number,
): offer is QuickTopupOffer {
  if (!isQuickTopupOfferUsable(offer) || !Number.isFinite(amount)) return false;
  return amount >= offer.min_amount && amount <= offer.max_amount;
}

/**
 * Backend từ chối offer Quick Topup (invalid / already used / expired) khi tạo đơn nạp.
 * Ưu tiên `reason` structured trong envelope (unwrap gắn vào Error object);
 * regex trên message chỉ là fallback tương thích backend cũ chưa trả reason.
 */
export function isQuickTopupOfferError(error: unknown): boolean {
  if ((error as { reason?: string } | null)?.reason === "quick_topup_offer_invalid") return true;
  const message = error instanceof Error ? error.message : String(error);
  return (
    /quick[\s_-]*topup/i.test(message) && /invalid|used|expired|hết hạn|đã dùng/i.test(message)
  );
}

export function requestQuickTopup(reason?: string, offer?: QuickTopupOffer) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(QUICK_TOPUP_EVENT, { detail: { reason, offer: stampQuickTopupOffer(offer) } }),
  );
}

export function quickTopupOfferFromError(error: unknown): QuickTopupOffer | undefined {
  return (error as { quick_topup_offer?: QuickTopupOffer } | null)?.quick_topup_offer;
}

/**
 * Đếm ngược Quick Topup. ≥ 1 giờ → hh:mm (đơn vị "giờ"); dưới 1 giờ → mm:ss
 * (đơn vị "phút"). Trả kèm `unit` để UI ghi rõ user đang xem giờ hay phút.
 */
export function formatQuickTopupCountdown(remainingSeconds: number): {
  text: string;
  unit: "" | "phút";
} {
  const s = Math.max(0, Math.floor(remainingSeconds));
  const pad = (n: number) => String(n).padStart(2, "0");
  if (s >= 3600) {
    // ≥ 1 giờ → nhúng đơn vị vào chuỗi cho rõ: "23h56p"
    return { text: `${Math.floor(s / 3600)}h${pad(Math.floor((s % 3600) / 60))}p`, unit: "" };
  }
  // dưới 1 giờ → mm:ss kèm nhãn "phút"
  return { text: `${pad(Math.floor(s / 60))}:${pad(s % 60)}`, unit: "phút" };
}

export function isInsufficientFundsError(error: unknown) {
  const message = (error instanceof Error ? error.message : String(error)).toLocaleLowerCase("vi");
  return (
    /insufficient[_\s-]*fund|hết số dư/.test(message) ||
    (message.includes("không đủ") && (message.includes("số dư") || message.includes("tiền")))
  );
}
